import { Context, createContext, ReactNode } from "react";
import { components } from "@apiSchema";
import {
    ChainDetails,
    iSocialLink,
    ReportsNameEnum,
    SocialLinkEnum
} from "@models/ChainDetails";
import { iSocialLinks, SocialLinks } from "@models/SocialLinks";
import { ChainSocialData, DEFAULT_CHAIN_SOCIAL_DATA } from "@apiHooks/useChainSocialData";
import Profile from "./Sections/Profile/Profile";
import Contacts from "./Sections/Contacts/Contacts";
import Menu from "./Sections/Menu/Menu";
import Launches from "./Sections/LTO/LTO";
import Locations from "./Sections/Locations/Locations";
import Brands from "./Sections/Brands/Brands";
import Socials from "./Sections/Socials/Socials";
import Strategy from "./Sections/Strategy/Strategy";
import { sanitizeUrl } from "@helpers";
import { AppId } from "@datassential/platform-ui-lib";

export const ChainPageScrollId = "ChainPageScroll";

class ChainDetailsDataContextData {
    data = new ChainDetails();
    sections: iSectionConfigItem[] = [];
    openGapReportPopup: () => void = () => null;
    downloadReports: (items: ReportsNameEnum[]) => void = () => null;
    socialLinksData: iSocialLinks = new SocialLinks();
    chainSocialData: ChainSocialData = DEFAULT_CHAIN_SOCIAL_DATA;
    isLoadingSocialLinks: boolean = true;
    isLoadingChainSocial: boolean = true;
    menuIncludes: string[] = [];
}


export const ChainDetailsDataContext: Context<ChainDetailsDataContextData> = createContext(
    new ChainDetailsDataContextData()
);

export enum ChainSectionsEnum {
    Unknown = '',
    Strategy = 'Strategy',
    Profile = 'Profile',
    Menu = 'Menu',
    Launches = 'Launches',
    Locations = 'Locations',
    Contacts = 'Contacts',
    Brand = 'Brand',
    Social = 'Social',
}

interface iSectionNoAccess {
    appId: AppId | null;
    getMessage: (productName: string) => string;
}

interface iSectionConfigItem {
    id: ChainSectionsEnum;
    title: string;
    checkHasDataField: keyof components['schemas']['SectionDataViewModel'] | null;
    noAccess: iSectionNoAccess;
    content: ReactNode;
}

const withSubscriptionTo = (productName: string) =>
    productName ? `with a subscription to ${productName}` : 'with a subscription';

const unlockWithSubscriptionTo = (appId: AppId): iSectionNoAccess => ({
    appId,
    getMessage: (productName) => `Unlock ${withSubscriptionTo(productName)}.`,
});

export const sectionsConfig: iSectionConfigItem[] = [
    {
        id: ChainSectionsEnum.Strategy,
        title: 'Strategy',
        checkHasDataField: null,
        content: <Strategy/>,
        noAccess: unlockWithSubscriptionTo(AppId.Chains),
    },
    {
        id: ChainSectionsEnum.Profile,
        title: 'Profile',
        checkHasDataField: null,
        content: <Profile/>,
        noAccess: unlockWithSubscriptionTo(AppId.Chains),
    },
    {
        id: ChainSectionsEnum.Menu,
        title: 'Menu',
        checkHasDataField: "hasMenu",
        content: <Menu/>,
        noAccess: unlockWithSubscriptionTo(AppId.MenuTrends),
    },
    {
        id: ChainSectionsEnum.Launches,
        title: 'Launches',
        checkHasDataField: "hasLto",
        content: <Launches/>,
        noAccess: unlockWithSubscriptionTo(AppId.Chains),
    },
    {
        id: ChainSectionsEnum.Locations,
        title: 'Locations',
        checkHasDataField: "hasLocation",
        content: <Locations/>,
        noAccess: unlockWithSubscriptionTo(AppId.Chains),
    },
    {
        id: ChainSectionsEnum.Contacts,
        title: 'Contacts',
        checkHasDataField: "hasContacts",
        content: <Contacts/>,
        noAccess: unlockWithSubscriptionTo(AppId.Chains),
    },
    {
        id: ChainSectionsEnum.Brand,
        title: 'Brand',
        checkHasDataField: "hasBrand",
        content: <Brands/>,
        noAccess: {
            appId: AppId.BrandPerformance,
            getMessage: (productName) =>
                `Unlock NPS scores and other key consumer ratings for this chain ${withSubscriptionTo(productName)}.`,
        },
    },
    {
        id: ChainSectionsEnum.Social,
        title: 'Social',
        checkHasDataField: null,
        content: <Socials/>,
        noAccess: unlockWithSubscriptionTo(AppId.Chains),
    },
];

export function getAvailableSections(data: components['schemas']['SectionDataViewModel']) {
    return sectionsConfig.filter(i => !i.checkHasDataField || data[i.checkHasDataField]);
}

interface iSectionVisibilityFlags {
    isSocialsLoading: boolean;
    socialsHasData: boolean;
    isSsbEnabled: boolean;
}

export function filterSectionsForVisibility(
    sections: iSectionConfigItem[],
    { isSocialsLoading, socialsHasData, isSsbEnabled }: iSectionVisibilityFlags
): iSectionConfigItem[] {
    let result = sections;
    if (isSocialsLoading || !socialsHasData) {
        result = result.filter(s => s.id !== ChainSectionsEnum.Social);
    }
    if (!isSsbEnabled) {
        result = result.filter(s => s.id !== ChainSectionsEnum.Strategy);
    }
    return result;
}

export function getSectionData(id: iSectionConfigItem['id']): iSectionConfigItem {
    return sectionsConfig.find(i => i.id === id) || {
        id: ChainSectionsEnum.Unknown,
        noAccess: { appId: null, getMessage: () => '' },
        title: 'undefined section',
        content: <>no content</>,
        checkHasDataField: null,
    };
}

export const HEADER_LARGE_HEIGHT = 213;
export const HEADER_SMALL_HEIGHT = 80;
export const APP_HEADER_HEIGHT = 60;

const getSocialName = (name: string): SocialLinkEnum => {
    switch (name) {
        case (SocialLinkEnum.facebook):
            return SocialLinkEnum.facebook;
        case (SocialLinkEnum.foursquare):
            return SocialLinkEnum.foursquare;
        case (SocialLinkEnum.instagram):
            return SocialLinkEnum.instagram;
        case (SocialLinkEnum.linkedin):
            return SocialLinkEnum.linkedin;
        case (SocialLinkEnum.pinterest):
            return SocialLinkEnum.pinterest;
        case (SocialLinkEnum.snapchat):
            return SocialLinkEnum.snapchat;
        case (SocialLinkEnum.tumblr):
            return SocialLinkEnum.tumblr;
        case (SocialLinkEnum.twitter):
            return SocialLinkEnum.twitter;
        case (SocialLinkEnum.wikipedia):
            return SocialLinkEnum.wikipedia;
        case (SocialLinkEnum.youtube):
            return SocialLinkEnum.youtube;
        case (SocialLinkEnum.zomato):
            return SocialLinkEnum.zomato;
        default:
            return SocialLinkEnum.none;
    }
}

const getSocialFollowersCount = (
    title: SocialLinkEnum,
    facebookFollowers?: number | null,
    instagramFollowers?: number | null,
): number | null => {
    if (title === SocialLinkEnum.facebook) return facebookFollowers ?? null;
    if (title === SocialLinkEnum.instagram) return instagramFollowers ?? null;
    return null;
};

export const mapSocialLinksToArray = (
    data: components['schemas']['FFSocialLinks'],
    facebookFollowers?: number | null,
    instagramFollowers?: number | null,
): iSocialLink[] => {
    return (Object.keys(data) as Array<keyof typeof data>)
        .map((key) => {
            const title = getSocialName(key);
            return {
                title,
                url: sanitizeUrl(data?.[key] ?? ''),
                followersCount: getSocialFollowersCount(title, facebookFollowers, instagramFollowers),
            };
        })
        .filter(i => !!i.url && i.title !== SocialLinkEnum.none);
}
